import * as React from "react"
import i18next from "i18next"
import { useNavigate, useLocation } from "react-router-dom"

import { createPagesRegistry, definePagesConfig, normalizePagesConfig } from "@onnia/pages"
import {
    createPagesI18n,
    renderPagesApp,
    PagesSidebarHeader,
    PagesFooter,
    usePagesTheme,
    type PagesDocContentModule,
} from "@onnia/pages/react"
import "@onnia/pages/styles.css"
import "./theme.css"
import { SIDEBAR_GROUP_ORDER } from "./sidebarGroupOrder"

type DocsLang = "en" | "es"
type DocsMetadataModule = Pick<PagesDocContentModule, "frontmatter" | "toc">

const config = normalizePagesConfig(
    definePagesConfig({
        site: {
            name: "OnnIA",
            defaultLocale: "es",
            locales: ["en", "es"],
            description: "OnnIA platform documentation.",
        },
        content: {
            root: "src/content",
            fallbackLocale: "es",
        },
    }),
)

const metadataModules = import.meta.glob<DocsMetadataModule>("./content/**/*.mdx", {
    eager: true,
    query: "?onnia-pages-metadata",
})

const contentLoaders = import.meta.glob<PagesDocContentModule>("./content/**/*.mdx")

const modules = Object.fromEntries(
    Object.entries(metadataModules).map(([path, metadata]) => {
        const loader = contentLoaders[path]
        if (!loader) {
            throw new Error(`[onnia-docs] Missing MDX content loader for ${path}`)
        }
        return [
            path,
            {
                frontmatter: metadata.frontmatter,
                toc: metadata.toc,
                default: React.lazy(async () => ({ default: (await loader()).default })),
            },
        ]
    }),
) as Record<string, PagesDocContentModule>

const registry = createPagesRegistry({
    modules,
    supportedLangs: config.site.locales as readonly DocsLang[],
    defaultLang: config.site.defaultLocale as DocsLang,
    isDev: import.meta.env.DEV,
    sidebarGroupOrder: SIDEBAR_GROUP_ORDER,
})

const sharedI18n = createPagesI18n<DocsLang>({
    fallbackLang: config.site.defaultLocale as DocsLang,
    supportedLangs: config.site.locales as readonly DocsLang[],
    instance: i18next,
})

function DocsHeader() {
    const { theme } = usePagesTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const src = theme === "dark" ? "/assets/logo-h-white.svg" : "/assets/logo-h.svg"
    const currentLang = (config.site.locales as readonly string[]).includes(location.pathname.split("/")[1])
        ? location.pathname.split("/")[1]
        : config.site.defaultLocale
    return (
        <PagesSidebarHeader
            title="OnnIA Documentación"
            onHome={() => navigate(`/${currentLang}`)}
            logo={
                <div style={{ position: "relative", paddingBottom: "1.25rem" }}>
                    <img src={src} alt="OnnIA" style={{ height: "2.75rem", width: "auto", maxWidth: "11rem" }} />
                    <span
                        style={{
                            position: "absolute",
                            bottom: "0.5rem",
                            left: "3.5rem",
                            fontSize: "10px",
                            fontWeight: 600,
                            padding: "1px 8px",
                            borderRadius: "9999px",
                            background: "oklch(0.627 0.265 303.9 / 15%)",
                            color: "var(--color-primary)",
                            border: "1px solid oklch(0.627 0.265 303.9 / 30%)",
                            lineHeight: 1.4,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Documentación
                    </span>
                </div>
            }
        />
    )
}

renderPagesApp({
    config,
    registry,
    i18n: sharedI18n,
    sidebarHeader: <DocsHeader />,
    search: true,
    footer: <PagesFooter termsHref="https://onnia.ai/es/terms" privacyHref="https://onnia.ai/es/privacy" />,
})
